<script lang="ts">
    import { onMount } from 'svelte'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'
    import * as yup from 'yup'

    import { validateSyncAt } from 'hydria/document_helpers'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { supportedLanguages } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sgoto, spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    import GenTextInput from 'hydria/components/Generics/GenTextInput.svelte'
    import GenUrlTitleInput from 'hydria/components/Generics/GenUrlTitleInput.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import ConsentBoolean from './ConsentBoolean.svelte'
    import ConsentMde from './ConsentMde.svelte'

    interface Props {
        route: any
    }

    let { route }: Props = $props()

    let docId = $state(route.result.path.params?.id)

    interface Document {
        id?: string
        lg: string
        title: string
        url_title: string
        active: boolean
        content_raw: string
        document_code?: string
        document_text?: string
        document_version?: string
        document_start?: string
    }

    const revSchema = yup.object().shape({
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => supportedLanguages.includes(value)),
        title: yup.string().required(),
        url_title: yup.string().required(),
        active: yup.boolean().required(),
        content_raw: yup.string(),
        document_code: yup.string().optional().nullable(),
        document_text: yup.string().optional().nullable(),
        document_version: yup.string().optional().nullable(),
        document_start: yup.string().optional().nullable(),
    })

    type Status = 'loading' | 'new' | 'ready' | 'not-found' | 'error' | 'saving'
    let status: Status = $state('loading' as Status)

    function makeEmpty(): Document {
        return {
            id: undefined,
            lg: 'fr',
            title: '',
            url_title: '',
            active: true,
            content_raw: '',
            document_code: undefined,
            document_text: undefined,
            document_version: undefined,
            document_start: undefined,
        }
    }

    let edit: Document = $state(makeEmpty())
    let saved: Document = $state(makeEmpty())

    let formIsValid = $derived(revSchema.isValidSync(edit))
    let revIsPristine = $derived(status != 'new' && isEqual(saved, edit))

    async function load(docId: string) {
        status = 'loading'
        if (docId == null || docId === 'new') {
            saved = makeEmpty()
            edit = makeEmpty()
            status = 'new'
        } else {
            let r: SimpleFetchResult

            try {
                r = await simpleFetch('GET', `/json/consent/1/document/${docId}`)
            } catch (e: any) {
                r = e
            }

            if (!r || !r.data || !r.data.success) {
                console.error('Failed to load, ', r)
                notify({ title: $_('Document'), text: $_('Le document n’existe pas, ou n’a pas pu être chargé.'), type: 'danger' })
                status = 'not-found'
                return
            }

            saved = r.data.item
            edit = Object.assign({}, saved)
            status = 'ready'
        }
    }

    onMount(() => {
        load(docId)
    })

    async function save() {
        let r: SimpleFetchResult

        status = 'saving'

        try {
            r = await simpleFetch('POST', '/json/consent/1/document', edit)
        } catch (e) {
            notify({ title: $_('Document'), text: $_('La sauvegarde est en échec.'), type: 'danger' })
            status = 'ready'
            return
        }
        status = 'ready'
        if (docId !== r.data.item.id) {
            docId = r.data.item.id
        }
        saved = r.data.item
        edit = Object.assign({}, saved)
    }

    let removeConfirm = $state(false)
    function preRemoveDocument() {
        if (status != 'ready') return
        removeConfirm = true
    }

    async function remove() {
        if (status != 'ready') return

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('DELETE', `/json/consent/1/document/${docId}`)
        } catch (e) {
            notify({ title: $_('Document'), text: $_('La supression est en échec.'), type: 'danger' })
            status = 'ready'
            return
        }
        if (r && r.data && r.data.success) {
            sgoto('/consent-documents')
        }
    }

    let urlTitleLoading = false
    async function urlTitle(lg: string, text: string) {
        urlTitleLoading = true
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', '/json/cms/1/url-title', {
                lg: lg,
                text: text,
            })
        } catch (e) {
            console.error(`Could not compute url title from text “${text}”:`, e)
            urlTitleLoading = false
            return ''
        }

        urlTitleLoading = false
        return r.data.clean
    }

    const crumbs = [{ txt: 'Documents de consentement', url: spath('/consent-documents') }, { txt: 'Édition' }]
</script>

<style>
    /* Layout */
    .col-container {
        position: relative;
        display: flex;
        align-items: stretch;
        gap: var(--hy-generic-gap);
        flex-direction: column;
    }
    .col-content,
    .col-save {
        display: flex;
        flex-direction: column;
        gap: var(--hy-generic-gap);
    }
    .col-content {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
    }
    .col-save {
        flex: 0 0 auto;
    }
    @media (min-width: 768px) {
        .col-container {
            flex-direction: row;
            align-items: start;
        }
        .col-content {
            flex: 1 1 0;
        }
        .col-edit {
            min-height: 619px;
        }
        .col-save {
            position: sticky;
            top: calc(var(--hy-topbar-height) + var(--hy-generic-gap));
            flex: 0 0 250px;
        }
    }

    @media print {
        .col-save {
            display: none !important;
        }
    }

    /* Styling */
    .card-header {
        background-color: var(--save-card-accent);
        color: var(--bs-light);
    }
</style>

<Breadcrumb {crumbs} />

{#if status == 'not-found' || status == 'error'}
    <div class="alert alert-danger d-flex align-items-center" role="alert">
        <div class="bi flex-shrink-0 me-2">
            <Icon inline icon="mdi:alert" />
        </div>
        <div>
            {#if status == 'not-found'}
                {$_('Le document demandé n’existe pas.')}
            {:else}
                {$_('Une erreur est survenue. Réessayez dans un moment.')}
            {/if}
        </div>
    </div>
{:else if status == 'loading'}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="col-container">
        <div class="col-content col-edit">
            <div class="card card-content">
                <div class="card-header text-bg-primary">{$_('Document')}</div>
                <div class="card-body d-grid gap-2">
                    <GenTextInput label={tgettext('Titre')} saved={saved.title} bind:value={edit.title} isValid={(v) => validateSyncAt(revSchema, 'title', { title: v })} helpText={undefined} />

                    <GenUrlTitleInput label={tgettext('Titre URL')} title={edit.title} saved={saved.url_title} bind:value={edit.url_title} isValid={(v) => validateSyncAt(revSchema, 'url_title', { url_title: v })} {urlTitle} lg={edit.lg} disabled={!['new', 'ready'].includes(status)} />

                    <LanguageSelector bind:value={edit.lg} />

                    <ConsentBoolean label={tgettext('Actif')} trueText={tgettext('Le document est publiquement accessible.')} falseText={tgettext('Le document n’est pas accessible.')} trueIsDanger={false} falseIsDanger={true} saved={saved.active} bind:value={edit.active} disabled={!['new', 'ready'].includes(status)} isValid={(v) => validateSyncAt(revSchema, 'active', { active: v })} />

                    <GenTextInput label={tgettext('Code')} saved={saved.document_code} bind:value={edit.document_code} isValid={(v) => validateSyncAt(revSchema, 'document_code', { document_code: v })} helpText={tgettext('Code interne de document. Si présent, est affiché en complément.')} />
                    <GenTextInput label={tgettext('Définition')} saved={saved.document_text} bind:value={edit.document_text} isValid={(v) => validateSyncAt(revSchema, 'document_text', { document_text: v })} helpText={tgettext('Nom interne du document. Si présent, est affiché en complément.')} />
                    <GenTextInput label={tgettext('Version')} saved={saved.document_version} bind:value={edit.document_version} isValid={(v) => validateSyncAt(revSchema, 'document_version', { document_version: v })} helpText={tgettext('Version du document. Si présente, est affichée en complément.')} />
                    <GenTextInput label={tgettext('Entrée en vigueur')} saved={saved.document_start} bind:value={edit.document_start} isValid={(v) => validateSyncAt(revSchema, 'document_start', { document_start: v })} helpText={tgettext('Date d’entrée en vigueur ; format reconnu : YYYY-MM-DD.  Si présent, est affiché en complément sous forme de texte selon la langue. Si présent, mais non-reconnu, sera affiché tel quel.')} />

                    <ConsentMde
                        saved={saved.content_raw}
                        bind:value={edit.content_raw}
                        disabled={!['new', 'ready'].includes(status)}
                        label={tgettext('Texte')}
                        helpText={null}
                        fieldName="content_raw"
                        isValid={(v) => {
                            if (v == null) return false
                            const clean = v.replaceAll(/<p><\/p>/gi, '')
                            return clean != ''
                        }}
                    />
                </div>
            </div>
        </div>
        <div class="col-save">
            <div class="card">
                <div class="card-header">{$_('Options du document')}</div>
                <div class="card-body d-grid gap-2">
                    <div class="btn-group btn-group d-flex">
                        <button type="button" class="btn btn-outline-secondary" data-tooltip={$_('Revenir à la version sauvegardée')} aria-label={$_('Revenir à la version sauvegardée')} onclick={() => load(docId)} disabled={status == 'new'}>
                            <Icon inline icon="mdi:undo" />
                        </button>

                        <button type="button" class="btn btn-primary" data-tooltip={$_('Sauvegarder')} aria-label={$_('Sauvegarder')} disabled={!formIsValid || !['new', 'ready'].includes(status)} onclick={() => save()}>
                            <Icon inline icon="mdi:content-save" />
                        </button>

                        <button type="button" class="btn btn-danger tt-left" data-tooltip={$_('Suppression')} aria-label={$_('Suppression')} disabled={status != 'ready'} onclick={() => preRemoveDocument()}>
                            <Icon inline icon="mdi:delete-forever" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}

{#if removeConfirm}
    <div class="modal modal-ds-records" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary">
                    <div>{$_('Supprimer ce document')}</div>
                </div>

                <div class="modal-body">
                    {$_('Êtes-vous certain de vouloir supprimer de document ?')}
                </div>

                <div class="modal-footer">
                    <button class="btn btn-danger" type="button" onclick={() => remove()}>
                        {$_('Supprimer')}
                    </button>
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (removeConfirm = false)}>{$_('Annuler')}</button>
                </div>
            </div>
        </div>
    </div>
    <div class="modal-backdrop" onclick={() => (removeConfirm = false)} aria-hidden="true"></div>
{/if}
